import React, { useState, useEffect } from 'react';
import { translations } from '../translations';
import apiService from '../services/apiService';

const WaistMeasurement = ({ patientData, taskId, language = 'en', onComplete, onClose }) => {
  const [measurement, setMeasurement] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const t = translations[language] || translations.en;

  // Charger les réponses existantes si elles existent
  useEffect(() => {
    const loadExistingResponses = async () => {
      if (!patientData || !patientData.patientId || !taskId) return;
      
      try {
        const response = await apiService.getQuestionnaireResponse(patientData.patientId, taskId);
        if (response.success && response.data) {
          const existingResponses = JSON.parse(response.data.responses);
          if (existingResponses.waist_circumference) {
            setMeasurement(existingResponses.waist_circumference.toString());
            setIsEditing(true);
          }
        }
      } catch (error) {
        console.error('Erreur chargement réponses existantes:', error);
      }
    };

    loadExistingResponses();
  }, [patientData, taskId]);

  const measurementValue = measurement ? parseFloat(measurement) : 0;
  const isValid = measurement && !isNaN(measurementValue) && measurementValue >= 40 && measurementValue <= 220;

  const handleSubmit = async () => {
    if (!isValid) return;

    setLoading(true);
    setError(null);

    try {
      const measurementValue = parseFloat(measurement);
      
      if (isEditing) {
        // Mode édition : utiliser la route de mise à jour
        await apiService.updateQuestionnaireResponse(
          patientData.patientId,
          taskId,
          'WAIST_MEASUREMENT',
          JSON.stringify({ waist_circumference: measurementValue }),
          measurementValue
        );
      } else {
        // Mode création : utiliser la route de soumission
        const responseData = {
          patientId: patientData.patientId,
          taskId: taskId,
          questionnaireType: 'WAIST_MEASUREMENT',
          responses: JSON.stringify({ waist_circumference: measurementValue }),
          score: measurementValue,
          language: language,
          submissionDate: (() => {
            const now = new Date();
            const year = now.getFullYear();
            const month = String(now.getMonth() + 1).padStart(2, '0');
            const day = String(now.getDate()).padStart(2, '0');
            const hours = String(now.getHours()).padStart(2, '0');
            const minutes = String(now.getMinutes()).padStart(2, '0');
            const seconds = String(now.getSeconds()).padStart(2, '0');
            const milliseconds = String(now.getMilliseconds()).padStart(3, '0');
            return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}Z`;
          })()
        };
        await apiService.submitQuestionnaire(responseData);
      }
      
      if (onComplete) {
        onComplete(measurementValue);
      }
    } catch (error) {
      console.error('Erreur soumission mesure:', error);
      setError(language === 'de' 
        ? 'Fehler beim Speichern der Messung'
        : 'Error saving measurement'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="questionnaire-container">
      <div className="questionnaire-content waist-measurement-modal">
        <div className="questionnaire-header">
          <h2 className="questionnaire-title">
            {language === 'de' 
              ? `Taillenumfang Messung ${isEditing ? '(Bearbeiten)' : ''}` 
              : `Waist Circumference Measurement ${isEditing ? '(Edit)' : ''}`
            }
          </h2>
          <button className="questionnaire-close" onClick={onClose}>×</button>
        </div>

        <div className="waist-measurement-content">
          {/* Image explicative */}
          <div className="waist-tutorial-section">
            <h3 className="tutorial-title">
              {language === 'de' 
                ? 'Wie misst man den Taillenumfang?' 
                : 'How to measure waist circumference?'
              }
            </h3>
            
            <div className="tutorial-image-container">
              <img 
                src="/images/waist_how.jpg" 
                alt={language === 'de' 
                  ? 'Anleitung zur Messung des Taillenumfangs' 
                  : 'Waist circumference measurement guide'
                }
                className="tutorial-image"
              />
            </div>

            <div className="tutorial-instructions">
              <h4>
                {language === 'de' ? 'Anweisungen:' : 'Instructions:'}
              </h4>
              <ul>
                <li>
                  {language === 'de' 
                    ? 'Stehen Sie aufrecht mit entspannten Schultern'
                    : 'Stand upright with relaxed shoulders'
                  }
                </li>
                <li>
                  {language === 'de' 
                    ? 'Legen Sie das Maßband horizontal um Ihre Taille auf Höhe des Nabels'
                    : 'Place the measuring tape horizontally around your waist at navel level'
                  }
                </li>
                <li>
                  {language === 'de' 
                    ? 'Das Maßband sollte parallel zum Boden verlaufen'
                    : 'The tape should be parallel to the floor'
                  }
                </li>
                <li>
                  {language === 'de' 
                    ? 'Atmen Sie normal aus und lesen Sie die Messung ab'
                    : 'Breathe out normally and take the reading'
                  }
                </li>
                <li>
                  {language === 'de' 
                    ? 'Das Maßband sollte eng anliegen, aber nicht einschneiden'
                    : 'The tape should be snug but not tight'
                  }
                </li>
              </ul>
            </div>
          </div>

          {/* Section de saisie */}
          <div className="measurement-input-section">
            <div className="input-group">
              <label htmlFor="waist-measurement" className="input-label">
                {language === 'de' 
                  ? 'Taillenumfang (cm):' 
                  : 'Waist circumference (cm):'
                }
              </label>
              <div className="input-container">
                <input
                  id="waist-measurement"
                  type="number"
                  value={measurement}
                  onChange={(e) => setMeasurement(e.target.value)}
                  placeholder={language === 'de' ? 'z.B. 85.5' : 'e.g. 85.5'}
                  className="measurement-input"
                  min="40"
                  max="220"
                  step="0.1"
                />
                <span className="input-unit">cm</span>
              </div>
            </div>

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            <div className="modal-actions">
              <button 
                onClick={handleSubmit}
                className="btn btn-primary"
                disabled={!isValid || loading}
              >
                {loading 
                  ? (language === 'de' ? 'Speichern...' : 'Saving...') 
                  : (language === 'de' ? 'Speichern' : 'Save')
                }
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WaistMeasurement; 